import { Controller } from "@hotwired/stimulus"

// Global keyboard shortcuts for the v2 nav. Only mounted when the full-screen
// nav is active, so the shortcut set ships and unships with it. The map value
// (single lowercase key -> path) comes from NavigationHelper#nav_shortcut_map,
// keeping bindings and the help modal in sync.
export default class extends Controller {
  static values = { map: Object }

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.onBeforeVisit = this.closeOverlays.bind(this)
    this.onBeforeRender = this.cleanupBackdrops.bind(this)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:before-render", this.onBeforeRender)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:before-render", this.onBeforeRender)
  }

  onKeydown(e) {
    const t = e.target
    const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable

    if (e.key === "Escape") {
      const sp = document.getElementById("searchPanel")
      if (sp && sp.classList.contains("show")) {
        window.bootstrap.Collapse.getOrCreateInstance(sp).hide()
        if (typing && t.blur) t.blur()
      }
      return
    }

    // Ctrl+/ — toggle the keyboard shortcuts help modal
    if (e.ctrlKey && e.key === "/") {
      e.preventDefault()
      this.toggleModal("shortcutsModal")
      return
    }

    // Ctrl+K — toggle the full-screen nav
    if (e.ctrlKey && (e.key === "k" || e.key === "K")) {
      e.preventDefault()
      this.toggleOffcanvas("navOffcanvas")
      return
    }

    // "/" — open the search panel (ignored while typing)
    if (!typing && e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault()
      this.showSearch()
      return
    }

    // Ctrl+<letter> — "go to" navigation
    if (e.ctrlKey && !e.metaKey && !e.altKey && !typing) {
      const dest = this.mapValue[e.key.toLowerCase()]
      if (dest) {
        e.preventDefault()
        window.Turbo.visit(dest)
      }
    }
  }

  toggleModal(id) {
    const el = document.getElementById(id)
    if (el) window.bootstrap.Modal.getOrCreateInstance(el).toggle()
  }

  toggleOffcanvas(id) {
    const el = document.getElementById(id)
    if (el) window.bootstrap.Offcanvas.getOrCreateInstance(el).toggle()
  }

  showSearch() {
    const el = document.getElementById("searchPanel")
    if (el) window.bootstrap.Collapse.getOrCreateInstance(el).show()
  }

  // Close any open overlay before a Turbo navigation so it doesn't linger.
  closeOverlays() {
    const oc = document.getElementById("navOffcanvas")
    const ocInstance = oc && window.bootstrap.Offcanvas.getInstance(oc)
    if (ocInstance) ocInstance.hide()

    const sp = document.getElementById("searchPanel")
    if (sp && sp.classList.contains("show")) {
      const spInstance = window.bootstrap.Collapse.getInstance(sp)
      if (spInstance) spInstance.hide()
    }
  }

  // Belt-and-suspenders: strip any stray backdrop/lock left mid-transition.
  cleanupBackdrops() {
    document.querySelectorAll(".offcanvas-backdrop, .modal-backdrop").forEach((b) => b.remove())
    document.body.classList.remove("offcanvas-backdrop", "modal-open")
    document.body.style.overflow = ""
    document.body.style.paddingRight = ""
  }
}
